import { useEffect, useRef, useState } from 'react'

interface Options<T> {
  serialize?: (value: T) => string
  deserialize?: (raw: string) => T
}

/**
 * Custom hook to persist state in sessionStorage (per-tab, cleared on tab close).
 *
 * Returns React's native useState setter (identity-stable across renders) and writes to
 * sessionStorage in a useEffect — safe to use as a dep in memos and effects.
 *
 * @param key The key to store the value under in sessionStorage
 * @param initialValue The initial value if no value exists in sessionStorage
 * @param options Optional custom serialize/deserialize for non-JSON-native types (Set, Map, Date, etc.)
 * @returns A tuple of [storedValue, setValue, isHydrated] — isHydrated is true when a value was restored from storage
 */
function useSessionStorage<T>(
  key: string,
  initialValue: T,
  options?: Options<T>
): [T, React.Dispatch<React.SetStateAction<T>>, boolean] {
  const serializeRef = useRef(options?.serialize ?? JSON.stringify)
  const deserializeRef = useRef(options?.deserialize ?? (JSON.parse as (raw: string) => T))

  const hydratedRef = useRef(false)

  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue
    }

    try {
      const item = window.sessionStorage.getItem(key)
      if (item !== null) {
        hydratedRef.current = true
        return deserializeRef.current(item)
      }
      return initialValue
    } catch (error) {
      console.warn(`Error reading sessionStorage key "${key}":`, error)
      return initialValue
    }
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.sessionStorage.setItem(key, serializeRef.current(storedValue))
    } catch (error) {
      console.warn(`Error setting sessionStorage key "${key}":`, error)
    }
  }, [key, storedValue])

  return [storedValue, setStoredValue, hydratedRef.current]
}

export default useSessionStorage
